/**
 * Shared limit definitions for the offline risk policy forms (PYMNT-2590 design 5.2 / 5.4).
 * Keys are the wire names of OfflineRiskPolicyLimitsDto; money limits travel as decimal strings in the
 * policy currency (USD), counts and ages as integers.
 */
export const LIMIT_FIELDS = [
    {key: 'offlineTxnCeiling', label: 'Transaction ceiling (USD)', money: true},
    {key: 'offlineSessionVelocityCount', label: 'Session velocity count', money: false},
    {key: 'offlineSessionVelocityAmount', label: 'Session velocity amount (USD)', money: true},
    {key: 'offlineAircraftExposureCap', label: 'Aircraft exposure cap (USD)', money: true},
    {key: 'offlineQueueMaxAgeSeconds', label: 'Queue max age (seconds)', money: false},
];

/** Mirrors upo.offline-policy.exposure-cap-confirmation-threshold-usd in the backoffice. */
export const EXPOSURE_CAP_CONFIRMATION_THRESHOLD_USD = 10000;

export const POLICY_CURRENCY = 'USD';

const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Effective limits after inheritance: the own value when set, otherwise the baseline value.
 * Both arguments are limit maps keyed by limit name (a form state or a policy's `limits`).
 */
export function resolveEffective(ownLimits, baselineLimits) {
    const effective = {};
    LIMIT_FIELDS.forEach(({key}) => {
        const own = numberOrNull(ownLimits ? ownLimits[key] : null);
        effective[key] = own !== null ? own : (baselineLimits ? numberOrNull(baselineLimits[key]) : null);
    });
    return effective;
}

/** Mirrors the config-service validation so the operator sees the problem before the request. */
export function validateEffective(effective, isBaseline) {
    if (isBaseline) {
        const missing = LIMIT_FIELDS.filter(({key}) => effective[key] === null);
        if (missing.length > 0) {
            return 'Every limit is required on the airline policy: ' + missing.map(m => m.label).join(', ');
        }
    }
    const {
        offlineTxnCeiling: ceiling,
        offlineSessionVelocityAmount: session,
        offlineAircraftExposureCap: exposure,
    } = effective;
    if (ceiling !== null && session !== null && ceiling > session) {
        return 'Transaction ceiling must not exceed the session velocity amount';
    }
    if (session !== null && exposure !== null && session > exposure) {
        return 'Session velocity amount must not exceed the aircraft exposure cap';
    }
    if (ceiling !== null && exposure !== null && ceiling > exposure) {
        return 'Transaction ceiling must not exceed the aircraft exposure cap';
    }
    if (effective.offlineSessionVelocityCount !== null && effective.offlineSessionVelocityCount < 1) {
        return 'Session velocity count must be at least 1';
    }
    if (effective.offlineQueueMaxAgeSeconds !== null && effective.offlineQueueMaxAgeSeconds < 1) {
        return 'Queue max age must be at least 1 second';
    }
    return null;
}

/**
 * The `limits` object of a PUT body from form values: money as a decimal string with two places,
 * counts as integers. Empty fields are omitted, which is how a tail override inherits.
 */
export function toLimitsRequest(form) {
    const limits = {};
    LIMIT_FIELDS.forEach(({key, money}) => {
        const value = numberOrNull(form[key]);
        if (value === null) {
            return;
        }
        limits[key] = money ? value.toFixed(2) : Math.trunc(value);
    });
    return limits;
}

export function formatLimit(value, money) {
    if (value === null || value === undefined) {
        return '—';
    }
    return money ? Number(value).toFixed(2) : String(value);
}

/** First block of the policy UUID, enough to match the id an aircraft or a deferred charge reports. */
export function shortId(id) {
    return id ? String(id).split('-')[0] : '—';
}

export function formatInstant(instant) {
    return instant ? new Date(instant).toLocaleString() : '—';
}
